/* @flow */
import * as React from "react";
import { connect } from "react-redux";
import { error } from "./customConsole";
import Spinner from "../Spinners/BrandSpinner";

import createDataSource, { type DataSource } from "./experimental_dataSource";

import dataLayer from "./dataLayer";
import { logCrash } from "./crashLog";

import type { Dispatch } from "../../redux/action";
import { trackOnTimeline } from "./Timing";

function getApiUrls() {

  return {
    api: window.API_URL,
    helpdesk: window.HELPDESK_API_URL,
    crash: window.CRASH_LOGGER_API_URL,
  };
}

const apiList = getApiUrls();

export const NEWS_LINK = window.NEWS_URL
// process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
//   ? process.env.NODE_ENV === "development"
//     ? "https://testnews.irontrade.com"
//     : "https://news.irontrade.com"
//   : null;

export const APP_NEWS_LINK =
    process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
        ? "https://ironnews.irontrade.com"
        : null;

const API =
    process.env.NODE_ENV === "development"
        ? apiList.api
        : window.API_URL || process.env.REACT_APP_API_URL;

const HELPDESK_API = apiList.helpdesk;

const CRASH_LOGGER_API =
    process.env.NODE_ENV === "development"
        ? apiList.crash
        : window.CRASH_LOGGER_API_URL || process.env.REACT_APP_CRASH_LOGGER_API_URL;

// const API =
//   process.env.NODE_ENV === "development"
//     ? process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
//       ? "https://testapp.irontrade.com"
//       : "https://testapp.siamoption.com"
//     : window.API_URL || process.env.REACT_APP_API_URL;

// const HELPDESK_API =
//   process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
//     ? "https://helpdesk.irontrade.com"
//     : "https://helpdesk.siamoption.com";

// const CRASH_LOGGER_API =
//   process.env.NODE_ENV === "development"
//     ? process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
//       ? "https://logtest.irontrade.com"
//       : "https://logtest.siamoption.com"
//     : window.CRASH_LOGGER_API_URL || process.env.REACT_APP_CRASH_LOGGER_API_URL;

const defaultOptions = {
  mode: "cors",
  credentials: "include",
};

type ApiCallMode = "helpdesk";

export function apiCall(method: string, options?: Object, mode?: ApiCallMode) {
  if (method.startsWith("/helpdesk")) {
    return trackOnTimeline(
        fetch(`${HELPDESK_API}${method.substring(9)}`, options),
        "API Call: " + method
    );
  }

  if (method.startsWith("crashlog://")) {
    return fetch(`${CRASH_LOGGER_API}/${method.substring(11)}`, {
      mode: "cors",
      ...options,
    });
  }

  return trackOnTimeline(
      fetch(`${API}${method}`, {
        ...defaultOptions,
        ...options,
      }),
      "API Call: " + method
  );
}

export function get(method: string, options?: Object) {
  return apiCall(method, options)
      .then((response) => {
        if (response.status !== 200) {
          throw new TypeError("Expected response with status 200");
        }

        return response;
      })
      .then((res) => res.json());
}

export function post(
    url: string,
    data: any,
    contentType?: string = "application/x-www-form-urlencoded; charset=UTF-8"
) {
  return new Promise((resolve, reject) => {
    let _data = data;

    // TODO: do this only for form-urlencoded
    if (typeof data === "object") {
      const keys = Object.keys(data);
      const fields = keys.reduce((res, key) => {
        res.push(`${key}=${encodeURIComponent(data[key])}`);
        return res;
      }, []);
      _data = fields.join("&");
    }

    let request = new XMLHttpRequest();
    request.responseType = "json";
    request.addEventListener("load", () => {
      const json = request.response;
      resolve(json);
    });
    request.addEventListener("error", () => {
      const json = request.response;
      reject(json);
    });
    request.open("POST", `${API}${url}`, true);
    request.setRequestHeader("Content-Type", contentType);
    request.send(_data);
  });
}

export function assertErrors(response: Object) {
  if (typeof response !== "object") {
    throw new TypeError("Response must be an object");
  }

  if (response.status !== "OK") {
    throw new Error(response.error || response.message);
  }

  return response;
}

export function reportServerFault(fault) {
  const details =
      typeof fault.response === "object"
          ? JSON.stringify(fault.response)
          : fault.response != null
          ? fault.response
          : "No additional details";

  // показываем, где поломалось api, в режиме разработки
  // с помощью нашей кастомной консоли
  if (process.env.NODE_ENV === "development") {
    error(
        `API Error: ${fault.status} ${fault.statusText} ${fault.endpoint}: ${fault.message}`
    );
  }

  try {
    dataLayer.push({
      event: "event-to-ga",
      eventCategory: "API Error",
      eventAction: `${fault.status} ${fault.statusText} ${fault.endpoint}: ${fault.message}`,
      eventLabel: details,
    });
  } catch (error) {
    console.warn("Failed to submit a bug report");
  }
}

export function mapDispatchToFetch(dispatch: Dispatch) {
  return {
    dispatch,
    async fetch(method: string, options?: Object): Object {
    // Обёртка над фетчем, но с побочным эффектом:
    // она развалидирует профиль и записывает ошибки.

    const response = await apiCall(method, options);

    if (response.status > 500) {
      try {
        const data = await response.json();

        logCrash(apiCall)({
          error: {
            message: data.message,
            info: {
              responseStatus: response.status,
              endpoint: method,
            },
          },
        });
      } catch (error) {}
    }

    if (response.status === 401) {
      dispatch({ type: "INVALIDATE_PROFILE" });
      throw new TypeError("Authentication needed");
    }

    if (options && options.unstable_dontInferResponse === true) {
      const data = await response.json();

      if (
          data &&
          data.status &&
          typeof data.status.toUpperCase === "function" &&
          data.status.toUpperCase() !== "OK"
      ) {
        logCrash(apiCall)({
          error: {
            message: data.message || data.msg,
            info: {
              responseStatus: data.status,
              endpoint: method,
            },
          },
        });
      }
      return data;
    }

    // Сервер не практикует корректные статусы,
    // поэтому мы будем парсить всё что дают.

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      const payload = {
        isVisible: false,
        fault: {
          endpoint: method,
          status: response.status,
          statusText: response.statusText,
          options,
          message: error.message || "Unable to parse response",
        },
      };

      console.warn(payload);

      dispatch({
        type: "RECORD_SERVER_FAULT",
        payload,
      });

      throw error;
    }

    if (data !== null) {
      // Так мы знаем, что ответ распарсился

      if (
          typeof data === "object" &&
          data.status === "ERROR" &&
          data.type === "verificationRequired"
      ) {
        return data;
      } else if (
          typeof data === "object" &&
          typeof data.status === "string" &&
          data.status &&
          typeof data.status.toUpperCase === "function" &&
          data.status.toUpperCase() !== "OK"
      ) {
        const payload = {
          isVisible: true,
          fault: {
            endpoint: method,
            status: response.status,
            statusText: response.statusText,
            options,
            message: data.message || data.msg,
            response: data,
          },
        };

        console.warn(payload);
        reportServerFault(payload.fault);

        dispatch({
          type: "RECORD_SERVER_FAULT",
          payload: {
            isVisible: true,
            fault: {
              endpoint: method,
              options,
              message: data.message || data.msg,
              response: data,
            },
          },
        });

        logCrash(apiCall)({
          error: {
            message: data.message || data.msg,
            info: {
              responseStatus: data.status,
              endpoint: method,
            },
          },
        });

        throw new TypeError("Unexpected server response");
      } else {
        // Делаем вид что всё хорошо
        return data;
      }
    }

    // До сюда дело не дойдёт.
  },
};
}

export const withFetch = connect(null, mapDispatchToFetch);

export const Server: DataSource<Get> = createDataSource({
  load(key) {
    return fetch(`${API}${key}`, defaultOptions).then((response) =>
        response.json()
    );
  },
});

class RawSessionStatus extends React.Component<
    { status: string },
    { available: ?boolean }
    > {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.status === "INVALID" && nextProps.status !== "INVALID") {
      return {
        status: nextProps.status,
        available: null,
      };
    } else {
      return {
        status: nextProps.status,
      };
    }
  }

  state = {
    status: null,
    available: null,
  };

  pending = false;

  async probe() {
    if (this.state.available == null && this.pending === false) {
      this.pending = true;

      try {
        const response = await fetch(`${API}/api/profile`, defaultOptions);
        this.pending = false;

        const profile = await response.json();

        this.setState({
          available: response.status === 200,
        });

        this.props.dispatch({
          type: "RECEIVE_PROFILE",
          payload: profile,
        });
      } catch (error) {
        this.pending = false;
        this.setState({
          available: false,
        });
      }
    }
  }

  render() {
    this.probe();

    if (this.state.available == null) {
      return <Spinner />;
    } else {
      return this.props.children(this.state.available, this.props.profile);
    }
  }
}

export const SessionStatus = connect((state) => ({
  status: state.dataRequirementsStatus.profile,
  profile: state.profile,
}))(RawSessionStatus);



// WEBPACK FOOTER //
// ./src/components/common/fetch.js